/**
 * BirFlyPermit — byte/string helpers with no runtime assumptions.
 * Must work identically in the browser, in Node and inside Apps Script's V8 runtime, so this
 * avoids `Buffer`, `TextEncoder`/`TextDecoder` and `btoa`/`atob`, none of which are guaranteed
 * to exist in all three.
 */

const B64URL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** UTF-8 encode a string to bytes by hand (no TextEncoder dependency). */
export function utf8Bytes(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const code = s.codePointAt(i)!;
    if (code > 0xffff) i++; // consumed a low surrogate as part of the code point
    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(out);
}

/** Bytes to lowercase hex. */
export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}

/** Base64url (no padding) encode, implemented by hand. */
export function b64urlEncode(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 3 <= bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out += B64URL_CHARS[(n >> 18) & 63] + B64URL_CHARS[(n >> 12) & 63] + B64URL_CHARS[(n >> 6) & 63] + B64URL_CHARS[n & 63];
  }
  const rem = bytes.length - i;
  if (rem === 1) {
    const n = bytes[i] << 16;
    out += B64URL_CHARS[(n >> 18) & 63] + B64URL_CHARS[(n >> 12) & 63];
  } else if (rem === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += B64URL_CHARS[(n >> 18) & 63] + B64URL_CHARS[(n >> 12) & 63] + B64URL_CHARS[(n >> 6) & 63];
  }
  return out;
}

/** Base64url (no padding) decode, implemented by hand. Throws on invalid input. */
export function b64urlDecode(s: string): Uint8Array {
  const clean = s.replace(/=+$/, '');
  const lookup = new Int16Array(256).fill(-1);
  for (let i = 0; i < B64URL_CHARS.length; i++) lookup[B64URL_CHARS.charCodeAt(i)] = i;
  const out: number[] = [];
  let bits = 0;
  let value = 0;
  for (let i = 0; i < clean.length; i++) {
    const v = lookup[clean.charCodeAt(i)];
    if (v < 0) throw new Error(`b64urlDecode: invalid character at ${i}`);
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}
